/**
 * One query behind a reply.
 *
 * A retrieval opens and closes: its header says who and what, so a row can be
 * told apart without opening it, and the body carries the lists. Each list
 * caps independently — expanding eighteen brands must not push the filters out
 * of reach.
 */

import React, { useState } from "react";
import PropTypes from "prop-types";
import { FiChevronDown, FiSliders, FiUsers } from "react-icons/fi";

/** How many rows a list shows before it offers the rest. */
const LIMIT = 5;

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** A capped list with its own count and its own expand. */
const Section = ({ label, count, items, children }) => {
  const [expanded, setExpanded] = useState(false);
  if (!items.length) return null;

  const overflow = items.length - LIMIT;
  const shown = expanded ? items : items.slice(0, LIMIT);

  return (
    <div className="lou-run__list">
      <div className="lou-run__listHead">
        <span className="lou-run__listLabel">{label}</span>
        <span className="lou-run__listCount">{count}</span>
      </div>
      {shown.map(children)}
      {overflow > 0 ? (
        <button
          type="button"
          className="lou-run__more"
          onClick={() => setExpanded((wasExpanded) => !wasExpanded)}
        >
          {expanded ? "Show less" : `Show all ${items.length}`}
        </button>
      ) : null}
    </div>
  );
};

/** Two or three names, then a count — a header is read, not scanned. */
const summarise = (names) => {
  if (!names.length) return "";
  if (names.length <= 2) return names.join(" and ");
  return `${names.slice(0, 2).join(", ")} and ${names.length - 2} more`;
};

const Fact = ({ label, value }) =>
  value ? (
    <div className="lou-run__fact">
      <span className="lou-run__facet">{label}</span>
      <span className="lou-run__value">{value}</span>
    </div>
  ) : null;

const Retrieval = ({
  index,
  open,
  onToggle,
  brands,
  competitors,
  segments,
  filters,
  modules,
  markets,
  period,
  comparison,
  base,
  statTest,
  mark,
}) => {
  const header = summarise(brands.map((b) => b.name));
  const subhead = [modules, markets.map((m) => m.name).join(", ")]
    .filter(Boolean)
    .join(" · ");

  const brandRow = (brand, competitor) => (
    <div className="lou-run__row" key={`${competitor ? "c" : "b"}-${brand.id}`}>
      <span className="lou-run__mark">{mark(brand)}</span>
      <span className="lou-run__name">{brand.name}</span>
      {competitor ? <span className="lou-run__tag">Compared</span> : null}
    </div>
  );

  return (
    <div className={`lou-run ${open ? "is-open" : ""}`}>
      <button
        type="button"
        className="lou-run__head"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className="lou-run__n">{index + 1}</span>
        <span className="lou-run__heading">
          <span className="lou-run__who">{header}</span>
          <span className="lou-run__what">{subhead}</span>
        </span>
        <span className={`lou-run__chevron ${open ? "is-open" : ""}`}>
          <FiChevronDown size={15} aria-hidden="true" />
        </span>
      </button>

      {open ? (
        <div className="lou-run__body">
          <Section
            label="Brands"
            count={plural(brands.length, "brand")}
            items={brands}
          >
            {(brand) => brandRow(brand, false)}
          </Section>

          <Section
            label="Compared against"
            count={plural(competitors.length, "brand")}
            items={competitors}
          >
            {(brand) => brandRow(brand, true)}
          </Section>

          <Section
            label="Segments"
            count={plural(segments.length, "segment")}
            items={segments}
          >
            {(segment) => (
              <div className="lou-run__row" key={`s-${segment.key}`}>
                <span className="lou-run__glyph lou-run__glyph--segment">
                  <FiUsers size={13} aria-hidden="true" />
                </span>
                <span className="lou-run__name">{segment.name}</span>
              </div>
            )}
          </Section>

          {/* A filter is a dimension with values chosen inside it, so the
              category names itself and the values sit beneath as chips. Run
              together on one line, nothing shows which came from where. */}
          <Section
            label="Filters"
            count={plural(filters.length, "filter")}
            items={filters}
          >
            {(filter) => (
              <div className="lou-run__filter" key={`f-${filter.key}`}>
                <div className="lou-run__row lou-run__row--bare">
                  <span className="lou-run__glyph lou-run__glyph--filter">
                    <FiSliders size={13} aria-hidden="true" />
                  </span>
                  <span className="lou-run__name lou-run__name--strong">
                    {filter.name}
                  </span>
                  {filter.values.length ? (
                    <span className="lou-run__listCount">
                      {plural(filter.values.length, "value")}
                    </span>
                  ) : null}
                </div>
                {filter.values.length ? (
                  <div className="lou-run__chips">
                    {filter.values.map((value) => (
                      <span className="lou-run__chip" key={value}>
                        {value}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            )}
          </Section>

          {period || comparison || base || statTest ? (
            <div className="lou-run__facts">
              <Fact label="Period" value={period} />
              <Fact label="Against" value={comparison} />
              <Fact label="Base" value={base} />
              <Fact label="Stat test" value={statTest} />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

Section.propTypes = {
  label: PropTypes.string,
  count: PropTypes.string,
  items: PropTypes.array,
  children: PropTypes.func,
};
Fact.propTypes = { label: PropTypes.string, value: PropTypes.string };

Retrieval.propTypes = {
  index: PropTypes.number,
  open: PropTypes.bool,
  onToggle: PropTypes.func,
  brands: PropTypes.array,
  competitors: PropTypes.array,
  segments: PropTypes.array,
  filters: PropTypes.array,
  modules: PropTypes.string,
  markets: PropTypes.array,
  period: PropTypes.string,
  comparison: PropTypes.string,
  base: PropTypes.string,
  statTest: PropTypes.string,
  mark: PropTypes.func,
};

export default Retrieval;
